import type { MemberTerminalRow } from '@routeback/core';
import { useToast } from '@routeback/ui';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { APP } from '../../app.config';
import { useApp } from '../../lib/app';
import { LegalLinks } from '../../lib/legal';
import { roleLabel } from '../../lib/roles';
import { supabase } from '../../lib/supabase';
import { ContactCard, SignaturesCard, SizesCard } from './MeSections';
import { NotificationsCard } from './NotificationsCard';
import { ShopCard } from './ShopCard';

/** Me: my name, contact + emergency contact, uniform sizes, terminals, what I've signed, settings, sign out. */
export function MeView() {
  const app = useApp();
  const toast = useToast();
  const [name, setName] = useState(app.userName);
  // The name the company set for me (on the invite or by a manager) — if there is one, it's theirs to change.
  const [companyName, setCompanyName] = useState<string | null>(null);
  useEffect(() => {
    if (!supabase) return;
    void supabase.from('memberships').select('display_name').eq('company_id', app.company.id).eq('user_id', app.userId).maybeSingle()
      .then(({ data }) => setCompanyName(((data as { display_name?: string } | null)?.display_name ?? '').trim()));
  }, [app.company.id, app.userId]);
  const mine = useLiveQuery(
    async () => (await app.db.table<MemberTerminalRow, string>('member_terminals').where('company_id').equals(app.company.id).toArray())
      .filter((m) => !m.deleted_at && m.user_id === app.userId),
    [app.db, app.company.id, app.userId],
    [] as MemberTerminalRow[],
  );
  const saveName = async () => {
    const n = name.trim();
    if (!n || !supabase) return;
    const { error } = await supabase.from('profiles').upsert({ user_id: app.userId, display_name: n });
    await supabase.auth.updateUser({ data: { display_name: n } });
    toast(error ? error.message : 'Name saved');
  };
  return (
    <div>
      <div className="card">
        <h2 style={{ fontSize: 19, margin: 0 }}>{companyName || app.userName}</h2>
        <div className="muted small">{roleLabel(app.role)} · {app.details.name}{app.email ? ` · ${app.email}` : ''}</div>
      </div>
      {app.mode === 'cloud' && companyName && (
        <div className="card">
          <div className="field" style={{ marginBottom: 0 }}><label>Your name (as coworkers see it)</label>
            <div>{companyName}</div>
            <span className="muted small">Set by {app.details.name}. {app.isManager ? 'Change it under People & invites.' : 'Ask a manager if it needs changing.'}</span>
          </div>
        </div>
      )}
      {app.mode === 'cloud' && companyName === '' && (
        <div className="card">
          <div className="field"><label>Your name (as coworkers see it)</label>
            <div className="row"><input className="grow" value={name} onChange={(e) => setName(e.target.value)} />
              <button className="btn btn-primary" disabled={!name.trim() || name.trim() === app.userName} onClick={() => void saveName()}>Save</button></div>
          </div>
        </div>
      )}
      {app.role !== 'mechanic' && <ContactCard />}
      {app.role !== 'mechanic' && <SizesCard />}
      {app.multiTerminal && (
        <>
          <div className="section-title">My terminals</div>
          <div className="list">
            {mine.length === 0 && <div className="list-item muted small">Your manager hasn't assigned terminals yet — use the switcher at the top.</div>}
            {mine.map((m) => (
              <div key={m.id} className="list-item">
                <div className="grow">{app.terminals.find((t) => t.id === m.terminal_id)?.name ?? 'Terminal'}</div>
                {m.is_home && <span className="chip on">home</span>}
              </div>
            ))}
          </div>
        </>
      )}
      {app.role === 'mechanic' && <ShopCard />}
      <NotificationsCard />
      {app.role !== 'mechanic' && <SignaturesCard />}
      <div className="section-title">Settings</div>
      <div className="list">
        <Link className="list-item settings-link" to="/settings/sync"><div className="grow">🔄 Sync &amp; data</div><span className="muted">›</span></Link>
        <Link className="list-item settings-link" to="/settings/account"><div className="grow">👤 Account</div><span className="muted">›</span></Link>
        {!app.isManager && <Link className="list-item settings-link" to="/manage/people"><div className="grow">👥 Coworkers</div><span className="muted">›</span></Link>}
      </div>
      {app.mode === 'cloud' && (
        <div className="card"><button className="btn btn-block" onClick={() => void app.signOut()}>Sign out</button></div>
      )}
      <div className="muted small" style={{ textAlign: 'center', margin: 12 }}>{APP.name} {APP.version}</div>
      <LegalLinks style={{ marginBottom: 24 }} />
    </div>
  );
}
